import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './Register.css';

const Register = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    mobile_number: '',
    password: '',
    confirm_password: '',
    address: '',
    city: '',
    pincode: '',
    school_id: ''
  });
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    try {
      setLoadingSchools(true);
      setError('');
      const response = await api.get('/schools');
      setSchools(response.data.schools);
      if (response.data.schools.length === 0) {
        setError('No schools available for registration. Please contact support.');
      }
    } catch (err) {
      setError('Failed to load schools. Please try again.');
      console.error(err);
    } finally {
      setLoadingSchools(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    if (!formData.school_id) {
      setError('Please select a school');
      return;
    }

    if (schools.length === 0) {
      setError('No schools available. Please refresh the page.');
      return;
    }

    setLoading(true);

    try {
      const { confirm_password, ...registerData } = formData;
      // Convert school_id to number
      registerData.school_id = parseInt(registerData.school_id);
      const response = await api.post('/auth/register', registerData);
      login(response.data.token, response.data.user);
      navigate('/schools');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <Header />
      <div className="page register-page">
        {/* Animated Background Elements */}
        <div className="auth-animated-bg">
          <div className="auth-floating-shape auth-shape-1"></div>
          <div className="auth-floating-shape auth-shape-2"></div>
          <div className="auth-floating-shape auth-shape-3"></div>
          <div className="auth-floating-shape auth-shape-4"></div>
          <div className="auth-gradient-orb auth-orb-1"></div>
          <div className="auth-gradient-orb auth-orb-2"></div>
        </div>

        <div className="container">
          <div className="auth-container">
            <div className="auth-header">
              <h1 className="auth-title">Create Account</h1>
              <p className="auth-subtitle">Join SLN Book World and get started today</p>
            </div>

            {error && (
              <div className="auth-error-message">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-form-group floating-label">
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  placeholder=" "
                  className="auth-form-input"
                />
                <label className="auth-form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  Full Name *
                </label>
                <div className="auth-input-underline"></div>
              </div>

              <div className="auth-form-group floating-label">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder=" "
                  className="auth-form-input"
                />
                <label className="auth-form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  Email Address *
                </label>
                <div className="auth-input-underline"></div>
              </div>

              <div className="auth-form-group floating-label">
                <input
                  type="tel"
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={handleChange}
                  required
                  placeholder=" "
                  className="auth-form-input"
                />
                <label className="auth-form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  Mobile Number *
                </label>
                <div className="auth-input-underline"></div>
              </div>

              <div className="auth-form-group floating-label">
                <select
                  name="school_id"
                  value={formData.school_id}
                  onChange={handleChange}
                  required
                  className="auth-form-input auth-form-select"
                  disabled={loadingSchools}
                >
                  <option value="">Select School *</option>
                  {schools.map((school) => (
                    <option key={school.id} value={school.id}>
                      {school.name} - {school.city} ({school.board_type})
                    </option>
                  ))}
                </select>
                <label className="auth-form-label auth-form-label-select">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                  </svg>
                  School *
                </label>
                <div className="auth-input-underline"></div>
              </div>

              <div className="auth-form-group floating-label">
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder=" "
                  className="auth-form-input"
                />
                <label className="auth-form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  Password *
                </label>
                <div className="auth-input-underline"></div>
              </div>

              <div className="auth-form-group floating-label">
                <input
                  type="password"
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  required
                  placeholder=" "
                  className="auth-form-input"
                />
                <label className="auth-form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  Confirm Password *
                </label>
                <div className="auth-input-underline"></div>
              </div>

              <div className="auth-form-group floating-label full-width">
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows="3"
                  placeholder=" "
                  className="auth-form-input auth-form-textarea"
                />
                <label className="auth-form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  Address (Optional)
                </label>
                <div className="auth-input-underline"></div>
              </div>

              <div className="auth-form-group floating-label">
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder=" "
                  className="auth-form-input"
                />
                <label className="auth-form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  City (Optional)
                </label>
                <div className="auth-input-underline"></div>
              </div>

              <div className="auth-form-group floating-label">
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  placeholder=" "
                  className="auth-form-input"
                />
                <label className="auth-form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  Pincode (Optional)
                </label>
                <div className="auth-input-underline"></div>
              </div>

              <div className="full-width">
                <button type="submit" className="auth-btn-submit" disabled={loading}>
                  {loading ? (
                    <>
                      <svg className="auth-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" strokeOpacity="0.25"></circle>
                        <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"></path>
                      </svg>
                      <span>Creating Account...</span>
                    </>
                  ) : (
                    <>
                      <span>Create Account</span>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14"></path>
                        <path d="M12 5l7 7-7 7"></path>
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </form>

            <div className="auth-divider">
              <span>Already have an account?</span>
            </div>

            <p className="auth-link">
              Sign in to your account <Link to="/login">here</Link>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Register;

